import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Student, DocumentFormData, DocumentFormErrors } from '../../types';
import { studentApi } from '../../services/api';
import SimpleDropdown from '../Schedule/SimpleDropdown';
import DocumentHistoryTable from './DocumentHistoryTable';
import './DocumentSubmissionModal.css';

interface DocumentSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const DocumentSubmissionModal: React.FC<DocumentSubmissionModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [formData, setFormData] = useState<DocumentFormData>({
    studentId: '',
    documentType: '',
    additionalNotes: '',
    sendEmail: false
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<DocumentFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New state for interactive modal features
  const [documentHistory, setDocumentHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  const [targetDocument, setTargetDocument] = useState<any | null>(null);
  const [replacementNotes, setReplacementNotes] = useState('');

  const documentTypes = [
    'Client Assessment Report',
    'Client IEP Report',
    'Communication Report',
    'Quarterly Report'
  ];

  const allowedFileTypes = ['.pdf', '.doc', '.docx'];

  useEffect(() => {
    if (isOpen) {
      fetchStudents();
      resetForm();
    }
  }, [isOpen]);

  const fetchStudents = async () => {
    try {
      const response = await studentApi.getAll({ active: true, limit: 1000 });
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    }
  }; 
 const resetForm = () => {
    setFormData({
      studentId: '',
      documentType: '',
      additionalNotes: '',
      sendEmail: false
    });
    setSelectedFile(null);
    setErrors({});
    setDocumentHistory([]);
    setHistoryError(null);
    setIsReplaceMode(false);
    setTargetDocument(null);
    setReplacementNotes('');
  };

  // Debounced function to fetch document history
  const fetchDocumentHistory = useCallback(async (studentId: string, documentType: string) => {
    if (!studentId || !documentType) {
      setDocumentHistory([]);
      return;
    }

    try {
      setLoadingHistory(true);
      setHistoryError(null);
      
      const { documentTransmittalsApi } = await import('../../services/api');
      const response = await documentTransmittalsApi.getHistory(parseInt(studentId), documentType);
      setDocumentHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching document history:', error);
      setHistoryError('Failed to load document history');
      setDocumentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Effect to fetch history when both student and document type are selected
  useEffect(() => {
    if (formData.studentId && formData.documentType) {
      const timeoutId = setTimeout(() => {
        fetchDocumentHistory(formData.studentId, formData.documentType);
      }, 300); // Debounce for 300ms

      return () => clearTimeout(timeoutId);
    } else {
      setDocumentHistory([]);
      setHistoryError(null);
    }
  }, [formData.studentId, formData.documentType, fetchDocumentHistory]);

  // Handle entering replace mode
  const handleReplaceDocument = (document: any) => {
    setIsReplaceMode(true);
    setTargetDocument(document);
    setSelectedFile(null);
    setReplacementNotes('');
  };

  // Handle exiting replace mode
  const handleCancelReplace = () => {
    setIsReplaceMode(false);
    setTargetDocument(null);
    setReplacementNotes('');
  };

  const validateForm = (): boolean => {
    const newErrors: DocumentFormErrors = {};

    if (!formData.studentId) {
      newErrors.studentId = 'Please select a student';
    }

    if (!formData.documentType) {
      newErrors.documentType = 'Please select a document type';
    }

    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
    } else {
      const fileExtension = '.' + selectedFile.name.split('.').pop()?.toLowerCase();
      if (!allowedFileTypes.includes(fileExtension)) {
        newErrors.file = 'Only PDF, DOC, and DOCX files are allowed';
      }

    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof DocumentFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);

    if (errors.file) {
      setErrors(prev => ({
        ...prev,
        file: undefined
      }));
    }
  }; 
 const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitFormData = new FormData();
      submitFormData.append('studentId', formData.studentId);
      submitFormData.append('documentType', formData.documentType);
      submitFormData.append('additionalNotes', formData.additionalNotes);
      submitFormData.append('sendEmail', formData.sendEmail.toString());
      submitFormData.append('uploadedBy', 'Current User');
      
      if (selectedFile) {
        submitFormData.append('file', selectedFile);
      }

      const { documentTransmittalsApi } = await import('../../services/api');
      let successMessage = 'Document submitted successfully! Processing will begin shortly.';

      // Handle replace mode
      if (isReplaceMode && targetDocument) {
        submitFormData.append('targetDocumentId', targetDocument.id.toString());
        submitFormData.append('replacementNotes', replacementNotes);
        successMessage = 'Document replaced successfully! Processing will begin shortly.';
        await documentTransmittalsApi.replace(submitFormData);
      } else {
        await documentTransmittalsApi.create(submitFormData);
      }

      toast.success(successMessage);
      onSubmit();
    } catch (error) {
      console.error('Error submitting document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit document';
      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const getSelectedStudent = () => {
    return students.find(student => student.id.toString() === formData.studentId);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };  if (
!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isReplaceMode ? 'Replace Document' : 'Add New Document'}</h2>
          <button
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        {isReplaceMode && targetDocument && (
          <div className="replace-mode-indicator">
            <div className="replace-info">
              <span className="replace-icon">🔄</span>
              <div className="replace-details">
                <strong>Replacing:</strong> {targetDocument.file_name}
                <br />
                <small>Uploaded by {targetDocument.uploaded_by} on {new Date(targetDocument.created_at).toLocaleDateString()}</small>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-cancel-replace"
              onClick={handleCancelReplace}
              disabled={isSubmitting}
            >
              Cancel Replace
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="document-form">
          <div className="form-group">
            <label htmlFor="student-select" className="form-label">
              Select Student *
            </label>
            <SimpleDropdown
              options={students.map(student => ({
                value: student.id.toString(),
                label: `${student.first_name} ${student.last_name}`
              }))}
              value={formData.studentId}
              onChange={(value) => handleInputChange('studentId', value)}
              placeholder="Search and select a student..."
              className={errors.studentId ? 'error' : ''}
            />
            {errors.studentId && (
              <span className="error-message">{errors.studentId}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="document-type" className="form-label">
              Document Type *
            </label>
            <select
              id="document-type"
              value={formData.documentType}
              onChange={(e) => handleInputChange('documentType', e.target.value)}
              className={`form-select ${errors.documentType ? 'error' : ''}`}
              disabled={isSubmitting}
            >
              <option value="">Select document type...</option>
              {documentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors.documentType && (
              <span className="error-message">{errors.documentType}</span>
            )}
          </div>     
     <div className="form-group">
            <label htmlFor="file-upload" className="form-label">
              Upload File *
            </label>
            <div className="file-upload-container">
              <input
                type="file"
                id="file-upload"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                className="file-input"
                disabled={isSubmitting}
              />
              <label htmlFor="file-upload" className="file-upload-label">
                <span className="file-upload-icon">📎</span>
                {selectedFile ? selectedFile.name : 'Choose file...'}
              </label>
              {selectedFile && (
                <div className="file-info">
                  <span className="file-size">{formatFileSize(selectedFile.size)}</span>
                </div>
              )}
            </div>
            <div className="file-help-text">
              Allowed formats: PDF, DOC, DOCX.
            </div>
            {errors.file && (
              <span className="error-message">{errors.file}</span>
            )}
          </div>

          {/* Document History Display */}
          {formData.studentId && formData.documentType && !isReplaceMode && (
            <DocumentHistoryTable
              documents={documentHistory}
              loading={loadingHistory}
              error={historyError}
              onReplace={handleReplaceDocument}
            />
          )}

          {/* Replacement Notes (only in replace mode) */}
          {isReplaceMode && (
            <div className="form-group">
              <label htmlFor="replacement-notes" className="form-label">
                Replacement Notes
              </label>
              <textarea
                id="replacement-notes"
                value={replacementNotes}
                onChange={(e) => setReplacementNotes(e.target.value)}
                className="form-textarea"
                rows={3}
                placeholder="Optional reason for replacing this document..."
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="additional-notes" className="form-label">
              Additional Notes
            </label>
            <textarea
              id="additional-notes"
              value={formData.additionalNotes}
              onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
              className="form-textarea"
              rows={4}
              placeholder="Optional message to include in the email..."
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.sendEmail}
                onChange={(e) => handleInputChange('sendEmail', e.target.checked)}
                className="form-checkbox"
                disabled={isSubmitting}
              />
              <span className="checkbox-text">Send email to student</span>
            </label>
            {formData.sendEmail && getSelectedStudent() && (
              <div className="email-info">
                Email will be sent to: <strong>{getSelectedStudent()?.email}</strong>
              </div>
            )}
          </div>          
{errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner"></span>
                  {isReplaceMode ? 'Replacing...' : 'Submitting...'}
                </>
              ) : (
                isReplaceMode ? 'Confirm Replacement' : 'Submit Document'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentSubmissionModal;
